import type { DeepReadonly } from "@itandi/itandi-bb-ui";
import clsx from "clsx";
import Link from "next/link";
import React from "react";
import { DealTypeLabel } from "~/components/common/parts/label/DealTypeLabel";
import { RelatedDealRowSkLink } from "~/components/specifics/property_deals/[id]/RelatedDealRowSkLink";
import { useUsingApplications } from "~/hooks/useUsingApplications";
import type { DealWithPrices } from "~/models/Deal";
import type { DealConstructionContext } from "~/models/DealConstruction";
import { isCanceled, PARTIALLY_FINISHED } from "~/models/Status";
import { PATH as DEAL_PATH } from "~/pages/deals/[id]";
import { PATH as BILLING_RENTEE_PATH } from "~/pages/deals/[id]/billing_rentees/edit";
import { PATH as BILLING_OWNER_PATH } from "~/pages/deals/[id]/billings/edit";
import { PATH as ESTIMATION_PATH } from "~/pages/deals/[id]/estimations";
import { toThousandSeparator } from "~/utils/CurrencySeparator";
import { ForwardStatus } from "./ForwardStatus";
import Styles from "./RelatedDealRow.module.scss";

type Props = {
	deal: DealWithPrices;
	propertyDealId: number;
	statuses: Record<number, string>;
};

const getVendorCompanyName = (deal: DeepReadonly<DealWithPrices>): string => {
	if (deal.originDealVendorCompany === null) {
		return "施工会社未選定";
	}
	if (deal.originDealVendorCompany.vendorCompany === null) {
		return deal.originDealVendorCompany.companyVendor?.name ?? "自社管理";
	}
	return deal.originDealVendorCompany.vendorCompany.name;
};

const profitStyles = {
	negative: Styles.ProfitNegative,
	positive: Styles.ProfitPositive,
	zero: Styles.ProfitZero
};

export const RelatedDealRow: React.FC<Props> = ({
	deal,
	propertyDealId,
	statuses
}: Props) => {
	const { useSK } = useUsingApplications();
	const notTargetDeal =
		isCanceled(deal.statusId) || deal.statusId === PARTIALLY_FINISHED;
	const dealEstimationContext = ((): DealConstructionContext => {
		// 非共有案件の時はdraft状態の見積りがあれば、draft状態の見積りを参照させる
		if (deal.originDealVendorCompany?.vendorCompany === null) {
			if (deal.dealEstimationHistoryContext.dealConstructions.length > 0) {
				return deal.dealEstimationHistoryContext;
			}
			return deal.dealEstimationContext;
		}
		// 非共有案件ではない時
		return deal.dealEstimationContext;
	})();
	const profit = React.useMemo(() => {
		if (deal.dealOwnerBillingContext.totalAmount === 0) {
			return null;
		}
		return (
			deal.dealOwnerBillingContext.sumOwnerAmount +
			deal.dealRenteeBillingContext.sumRenteeAmount -
			dealEstimationContext.totalAmount
		);
	}, [deal, dealEstimationContext]);
	const profitRate = React.useMemo(() => {
		if (profit === null || deal.dealOwnerBillingContext.totalAmount === 0) {
			return null;
		}
		return Math.round(
			(profit /
				(deal.dealOwnerBillingContext.sumOwnerAmount +
					deal.dealRenteeBillingContext.sumRenteeAmount)) *
				100
		);
	}, [profit, deal]);
	const profitStyle = React.useMemo(() => {
		if (profitRate === null) {
			return profitStyles.zero;
		}
		if (profitRate > 0) {
			return profitStyles.positive;
		}
		if (profitRate < 0) {
			return profitStyles.negative;
		}
		return profitStyles.zero;
	}, [profitRate]);

	const statusName = statuses[deal.statusId] ?? "不明なステータス";

	return (
		<div className={Styles.DealRow}>
			<div className={Styles.DealVendorBlock}>
				<div className={Styles.DealTypeBlock}>
					<DealTypeLabel
						dealSubtype={deal.dealSubtype}
						dealType={deal.dealType}
					/>
				</div>
				<h4 className={Styles.ContextLabel}>案件依頼先</h4>
				<Link className={Styles.DealLink} href={DEAL_PATH(deal.id)}>
					{getVendorCompanyName(deal)}
				</Link>
			</div>
			<div className={Styles.DealStatusBlock}>
				<h4 className={Styles.ContextLabel}>現在の手続きステータス</h4>
				<span className={Styles.ContentText}>{statusName}</span>
				<ForwardStatus deal={deal} propertyDealId={propertyDealId} />
			</div>
			<div
				className={clsx(
					Styles.DealEstimationBlock,
					notTargetDeal && Styles.NotTarget
				)}
			>
				<h4 className={Styles.ContextLabel}>施工会社からの見積額</h4>
				{dealEstimationContext.dealConstructions.length > 0 ? (
					<>
						<div>
							<Link
								className={Styles.ContentLink}
								href={ESTIMATION_PATH(deal.id)}
								target="_blank"
							>
								¥{toThousandSeparator(dealEstimationContext.totalAmount)}
							</Link>
						</div>
						<div className={Styles.SubContentText}>
							税込:¥
							{toThousandSeparator(dealEstimationContext.totalAmountWithTax)}
						</div>
					</>
				) : (
					<span className={clsx(Styles.ContentText, Styles.Empty)}>
						未作成
						<div className={Styles.SubContentText}>-</div>
					</span>
				)}
			</div>
			<div
				className={clsx(
					Styles.DealBillingBlock,
					notTargetDeal && Styles.NotTarget
				)}
			>
				<h4 className={Styles.ContextLabel}>入居者への請求額</h4>
				{deal.dealRenteeBillingContext.dealConstructions.length > 0 ? (
					<>
						<div>
							<Link
								className={Styles.ContentLink}
								href={BILLING_RENTEE_PATH(deal.id)}
								target="_blank"
							>
								¥
								{toThousandSeparator(
									deal.dealRenteeBillingContext.sumRenteeAmount
								)}
							</Link>
						</div>
						<div className={Styles.SubContentText}>
							税込:¥
							{toThousandSeparator(
								deal.dealRenteeBillingContext.sumRenteeAmountWithTax
							)}
						</div>
					</>
				) : (
					<span className={clsx(Styles.ContentText, Styles.Empty)}>
						未作成
						<div className={Styles.SubContentText}>-</div>
					</span>
				)}
			</div>
			<div
				className={clsx(
					Styles.DealBillingBlock,
					notTargetDeal && Styles.NotTarget
				)}
			>
				<h4 className={Styles.ContextLabel}>オーナーへの請求額</h4>
				{deal.dealOwnerBillingContext.dealConstructions.length > 0 ? (
					<>
						<div>
							<Link
								className={Styles.ContentLink}
								href={BILLING_OWNER_PATH(deal.id)}
								target="_blank"
							>
								¥
								{toThousandSeparator(
									deal.dealOwnerBillingContext.sumOwnerAmount
								)}
							</Link>
						</div>
						<div className={Styles.SubContentText}>
							税込:¥
							{toThousandSeparator(
								deal.dealOwnerBillingContext.sumOwnerAmountWithTax
							)}
						</div>
					</>
				) : (
					<span className={clsx(Styles.ContentText, Styles.Empty)}>
						未作成
						<div className={Styles.SubContentText}>-</div>
					</span>
				)}
			</div>
			<div
				className={clsx(Styles.ProfitBlock, notTargetDeal && Styles.NotTarget)}
			>
				<h4 className={Styles.ContextLabel}>利益額</h4>
				<div className={Styles.ProfitText}>
					<span className={profitStyle}>
						{profit === null ? "-" : `¥${toThousandSeparator(profit)}`}
					</span>
				</div>
			</div>
			<div
				className={clsx(
					Styles.ProfitRateBlock,
					notTargetDeal && Styles.NotTarget
				)}
			>
				<h4 className={Styles.ContextLabel}>利益率</h4>
				<div className={Styles.ProfitText}>
					<span className={profitStyle}>
						{profitRate === null ? "-" : toThousandSeparator(profitRate)}
					</span>
					<span className={Styles.Unit}>%</span>
				</div>
			</div>
			{useSK ? (
				<RelatedDealRowSkLink deal={deal} notTargetDeal={notTargetDeal} />
			) : null}
		</div>
	);
};
